/**
 * Smoke — a volume that actually blocks line of sight.
 *
 * A smoke grenade that only draws sprites is a lie: the player walks into it, the
 * squad keeps shooting them through it, and the attachment is a visual effect
 * costing a grenade slot. So the question the brief asks — can `physics.visible`
 * and AI perception be made to respect it — is the whole feature, and the answer
 * is yes, with one caveat that is written down below rather than hidden.
 *
 * ## How the blocking works
 *
 * Every consumer of line of sight in this game funnels into one of two calls:
 *
 *   - `physics.visible(from, to, ignore)` — `Enemy._perceive` (both the chest and
 *     the head probe), `AIDirector._pickSpawn`, `AIDirector.pickFlankPoint`, and
 *     the scorestreak target queries.
 *   - `physics.raycast(...)` with a world mask — `Minimap._lineOfSight`, and the
 *     ballistics path.
 *
 * The first is wrapped here, once, at construction: the original is kept and the
 * wrapper adds `&& !smokeBlocks(from, to)`. The second is deliberately *not*
 * wrapped, because it is also how bullets travel and smoke does not stop rounds.
 * The one raycast consumer that genuinely is a sight test — the minimap's reveal
 * sweep — asks this module directly instead (`src/ui/Minimap.js`, which this agent
 * owns).
 *
 * The caveat, stated plainly: the wrap is a mutation of a module shared by every
 * feature. It is guarded, restores the original on `dispose()`, and short-circuits
 * on `count === 0` so an unsmoked frame pays one integer test. The alternative —
 * registering the cloud as a real collider — was rejected because `sweepCapsule`
 * and `raycast` filter on the same `WORLD|PROP` masks, so the cloud would also
 * stop bullets and wall the player out of their own smoke.
 *
 * ## Occlusion model
 *
 * Each cloud is a sphere with a density. A sight line is blocked when the total
 * optical depth along it — chord length through each sphere, times that sphere's
 * density — exceeds `blockDepth`. Summing chords means two overlapping puffs block
 * where one thin one does not, and a line that clips the very edge of a cloud stays
 * open, which is what makes the edges of a smoke usable cover rather than a wall
 * with a hard boundary.
 *
 * ## Thermal
 *
 * Thermal optics defeat smoke. That is the real-world property of the device and it
 * is the reason to carry both; see `Thermal.js` for how the highlight is drawn over
 * the cloud while walls still occlude it.
 */

export const TUNING = {
  /** Optical depth (metres x density) above which a sight line is blocked. */
  blockDepth: 2.2,

  /** Cloud life cycle, seconds. */
  bloomTime: 1.5, // canister to full radius
  holdTime: 11.0, // full strength
  fadeTime: 5.0, // dissipating

  radius: 4.2, // m at full bloom
  startRadius: 0.7,
  density: 1.0,

  /** Visual: soft particles per second per cloud while blooming, and while held. */
  // ~100 live puffs per cloud in the hold phase. That is what it takes for the
  // picture to agree with the rule: a volume the AI cannot see through has to be
  // a volume the player cannot see through either, and a thin one is worse than
  // no smoke at all because it lies about where the cover is.
  puffsPerSecondBloom: 48,
  puffsPerSecond: 20,
  puffLife: 5.0,
  puffSizeStart: 2.2,
  puffSizeEnd: 4.8,
  puffAlpha: 0.92,
  /** Atlas cell for the billowing smoke sprite. Mirrors `CELL.SMOKE` in
   *  `src/fx/Particles.js`; taken as a literal rather than imported, because a
   *  feature module may not import another feature module. */
  atlasCell: 0,
  /**
   * Rise and drift, m/s. Deliberately tiny. A screening smoke is not a bonfire:
   * anything above ~0.2 m/s and the puffs climb out of the sphere over their own
   * lifetime, so what the player sees is a thin plume above an invisible volume
   * that is still blocking sight lines — the exact mismatch between the picture
   * and the rule that makes a smoke feel broken.
   */
  puffRise: 0.14,
  puffDrift: 0.5,

  /**
   * Hard cap. At ~100 live puffs each, four clouds is 400 soft particles — a third
   * of the pool on 'high' — and the launcher only ever carries two rounds, so this
   * is headroom rather than a limit anyone will meet in play.
   */
  maxClouds: 4,
};

export class SmokeField {
  constructor(ctx, system) {
    this.ctx = ctx;
    this.system = system;
    this.clouds = [];
    for (let i = 0; i < TUNING.maxClouds; i++) {
      this.clouds.push({ live: false, x: 0, y: 0, z: 0, r: 0, t: 0, density: 0, seed: 0, emit: 0 });
    }
    this.count = 0;
    this.spawned = 0;
    this.blockedQueries = 0;

    this._wrapVisible();
  }

  /* ---------------------------- physics.visible ---------------------------- */

  _wrapVisible() {
    const phys = this.ctx.physics;
    if (!phys || typeof phys.visible !== 'function' || phys.__smokeWrapped) {
      this.wrapped = false;
      return;
    }
    const original = phys.visible.bind(phys);
    this._originalVisible = phys.visible;
    const self = this;
    phys.visible = function wrappedVisible(from, to, ignoreActor) {
      if (!original(from, to, ignoreActor)) return false;
      // Fast path: no smoke in the world, one integer test and out.
      if (self.count === 0) return true;
      if (self.blocks(from, to)) {
        self.blockedQueries++;
        return false;
      }
      return true;
    };
    phys.__smokeWrapped = true;
    this.wrapped = true;
  }

  /* ------------------------------- deployment ------------------------------ */

  /** Start a cloud. Returns the slot, or null if all of them are already alight. */
  deploy(x, y, z) {
    let slot = this.clouds.find((c) => !c.live);
    if (!slot) {
      // Recycle the oldest rather than dropping the request: a player who threw it
      // must always see something happen.
      slot = this.clouds.reduce((a, b) => (a.t > b.t ? a : b));
    } else {
      this.count++;
    }
    slot.live = true;
    slot.x = x;
    slot.y = y;
    slot.z = z;
    slot.t = 0;
    slot.r = TUNING.startRadius;
    slot.density = TUNING.density;
    slot.seed = Math.random() * 1000;
    slot.emit = 0;
    this.spawned++;
    return slot;
  }

  clear() {
    for (const c of this.clouds) c.live = false;
    this.count = 0;
  }

  /* --------------------------------- query --------------------------------- */

  /** Optical depth along a segment: metres of smoke, weighted by density. */
  depth(from, to) {
    if (this.count === 0) return 0;
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const dz = to.z - from.z;
    const len = Math.hypot(dx, dy, dz);
    if (len < 1e-4) return 0;
    const ix = dx / len;
    const iy = dy / len;
    const iz = dz / len;
    let total = 0;
    for (let i = 0; i < this.clouds.length; i++) {
      const c = this.clouds[i];
      if (!c.live || c.density <= 0.02) continue;
      // Chord of a sphere against the segment, clamped to the segment's extent.
      const ox = c.x - from.x;
      const oy = c.y - from.y;
      const oz = c.z - from.z;
      const tca = ox * ix + oy * iy + oz * iz;
      const d2 = ox * ox + oy * oy + oz * oz - tca * tca;
      const r2 = c.r * c.r;
      if (d2 >= r2) continue;
      const thc = Math.sqrt(r2 - d2);
      const t0 = Math.max(0, tca - thc);
      const t1 = Math.min(len, tca + thc);
      if (t1 <= t0) continue;
      total += (t1 - t0) * c.density;
    }
    return total;
  }

  /** True if a sight line between two world points is smoked out. */
  blocks(from, to) {
    return this.depth(from, to) >= TUNING.blockDepth;
  }

  /** True if a world point is inside any live cloud. Used by the thermal rule. */
  contains(p) {
    for (let i = 0; i < this.clouds.length; i++) {
      const c = this.clouds[i];
      if (!c.live) continue;
      const dx = p.x - c.x;
      const dy = p.y - c.y;
      const dz = p.z - c.z;
      if (dx * dx + dy * dy + dz * dz < c.r * c.r) return true;
    }
    return false;
  }

  /* --------------------------------- frame --------------------------------- */

  update(dt) {
    if (this.count === 0) return;
    const total = TUNING.bloomTime + TUNING.holdTime + TUNING.fadeTime;
    let live = 0;
    for (let i = 0; i < this.clouds.length; i++) {
      const c = this.clouds[i];
      if (!c.live) continue;
      c.t += dt;
      if (c.t >= total) {
        c.live = false;
        continue;
      }
      live++;
      if (c.t < TUNING.bloomTime) {
        const k = c.t / TUNING.bloomTime;
        // Ease out: a canister dumps most of its volume immediately.
        const e = 1 - (1 - k) * (1 - k);
        c.r = TUNING.startRadius + (TUNING.radius - TUNING.startRadius) * e;
        c.density = TUNING.density * e;
      } else if (c.t < TUNING.bloomTime + TUNING.holdTime) {
        c.r = TUNING.radius;
        c.density = TUNING.density;
      } else {
        const k = (c.t - TUNING.bloomTime - TUNING.holdTime) / TUNING.fadeTime;
        // Radius keeps creeping as it thins, so it dissipates rather than shrinks.
        c.r = TUNING.radius * (1 + k * 0.25);
        c.density = TUNING.density * (1 - k) * (1 - k);
      }
    }
    this.count = live;
  }

  /**
   * Visuals. Reuses the FX system's pooled, sun-lit, depth-faded soft particles
   * rather than adding a second smoke renderer: they already handle the two hard
   * parts (soft intersection with world geometry, and lighting the puff by the
   * same key light as everything else), and a fifteen-second cloud is exactly the
   * long-lived case their GPU-analytic integration is cheapest at.
   */
  present(dt) {
    if (this.count === 0) return;
    const particles = this.ctx.fx?.particles;
    if (!particles) return;

    for (let i = 0; i < this.clouds.length; i++) {
      const c = this.clouds[i];
      if (!c.live || c.density <= 0.05) continue;
      const blooming = c.t < TUNING.bloomTime;
      const rate = blooming ? TUNING.puffsPerSecondBloom : TUNING.puffsPerSecond * c.density;
      c.emit += rate * dt;
      let n = Math.floor(c.emit);
      if (n <= 0) continue;
      c.emit -= n;
      if (n > 6) n = 6; // never let a frame hitch dump a whole cloud at once
      for (let k = 0; k < n; k++) {
        // Uniformly inside the sphere, biased slightly toward the middle so the
        // core stays opaque and the rim stays ragged.
        const u = Math.random();
        const rr = c.r * Math.cbrt(u) * 0.82;
        const th = Math.random() * Math.PI * 2;
        const ph = Math.acos(2 * Math.random() - 1);
        const sx = c.x + rr * Math.sin(ph) * Math.cos(th);
        const sy = c.y + rr * Math.cos(ph) * 0.75;
        const sz = c.z + rr * Math.sin(ph) * Math.sin(th);
        const grey = 0.72 + Math.random() * 0.12;
        particles
          .beginSoft()
          .pos(sx, sy, sz)
          .vel(
            (Math.random() - 0.5) * TUNING.puffDrift,
            TUNING.puffRise * (0.5 + Math.random()),
            (Math.random() - 0.5) * TUNING.puffDrift
          )
          .life(TUNING.puffLife)
          .drag(0.7)
          .grav(-0.02)
          .size(TUNING.puffSizeStart, TUNING.puffSizeEnd)
          .roll(Math.random() * Math.PI * 2, (Math.random() - 0.5) * 0.35)
          .atlas(TUNING.atlasCell)
          .colA(grey, grey, grey * 1.02, TUNING.puffAlpha * c.density)
          .colB(grey * 0.86, grey * 0.86, grey * 0.9)
          .fade(0.18, 1.4)
          .go();
      }
    }
  }

  report() {
    return {
      active: this.count,
      deployed: this.spawned,
      /** Proof the wrap took: false means AI perception is *not* respecting smoke. */
      visibleWrapped: !!this.wrapped,
      losBlocked: this.blockedQueries,
      clouds: this.clouds
        .filter((c) => c.live)
        .map((c) => ({ x: +c.x.toFixed(1), y: +c.y.toFixed(1), z: +c.z.toFixed(1), r: +c.r.toFixed(2), d: +c.density.toFixed(2) })),
    };
  }

  dispose() {
    this.clear();
    const phys = this.ctx.physics;
    if (this.wrapped && phys && this._originalVisible) {
      phys.visible = this._originalVisible;
      phys.__smokeWrapped = false;
    }
  }
}
